import { ChargeItem, Extension } from "@medplum/fhirtypes";

import { replaceChargeItemNoteExtensions } from "@/billing/utils/charge-item-notes";
import { Extensions } from "@/fhir/enums/extensions";
import { billerEditedModifiersExtension } from "@/utils/billing";

/** What a biller changed about one row, in the shape the charge has to record it. */
export type BillerSectionChargeEdit = {
  /**
   * The modifiers the picker session ended with. An empty list clears the charge's own modifiers
   * and keeps them cleared, because the write marks the charge as edited either way.
   */
  modifiers: string[];
  /** The units the picker session ended with. Always written, since the charge is the row's units. */
  quantity: number;
  /**
   * The Billing Note the picker session ended with. Always written, since the charge is what the
   * row's notes are read from. A note cleared to blank leaves the charge carrying none.
   */
  billerNotes: string;
  /** The Procedure Note the picker session ended with. Written on the same terms as `billerNotes`. */
  providerNotes: string;
  /**
   * The biller-owned Procedure this edit's diagnoses belong to, as a reference string. An edit that
   * only changed the modifiers writes no resources, so it has no Procedure to point at and omits
   * this field.
   */
  procedureReference?: string;
};

/**
 * Returns a copy of the charge with the biller's edit recorded on it.
 *
 * Only three fields change. An edit cannot alter anything else on the charge, such as its status,
 * its price, its payer, or the identifier its conditional create is keyed on.
 *
 * `extension` keeps every extension with a different url and carries exactly the modifiers passed
 * in, trimmed, with blanks dropped. It also carries the biller-edited marker, so an edit that
 * cleared every modifier is not read back as a charge nobody has edited. The marker is why the
 * field is always written: an edited charge always has at least that one extension. The session's
 * two notes are written the same way, replacing whatever the charge carried.
 *
 * `supportingInformation` puts the biller-owned Procedure first and keeps the references already
 * there after it. The order matters: the row builder reads the first Procedure that resolves, so
 * the first entry is the one whose diagnoses the row lists. Keeping the rest prevents a second
 * charge from appearing, because capture skips a Procedure that any charge already names, and the
 * charge's original Procedure has to stay named for that to work.
 */
export function applyBillerSectionChargeEdit(
  chargeItem: ChargeItem,
  { modifiers, quantity, procedureReference, billerNotes, providerNotes }: BillerSectionChargeEdit,
): ChargeItem {
  const {
    extension: _replacedExtensions,
    supportingInformation: _repointed,
    quantity: _replacedQuantity,
    ...withoutEditedFields
  } = chargeItem;

  const modifierExtensions: Extension[] = modifiers
    .map((modifier) => modifier.trim())
    .filter(Boolean)
    .map((modifier) => ({ url: Extensions.ChargeItemProcedureModifiers, valueString: modifier }));

  const editedModifiersMarker = billerEditedModifiersExtension();

  const extension = replaceChargeItemNoteExtensions(
    [
      ...(chargeItem.extension ?? []).filter(
        ({ url }) =>
          url !== Extensions.ChargeItemProcedureModifiers && url !== editedModifiersMarker.url,
      ),
      ...modifierExtensions,
      editedModifiersMarker,
    ],
    { billerNotes, providerNotes },
  );

  const existingSupportingInformation = chargeItem.supportingInformation ?? [];
  const supportingInformation = procedureReference
    ? [
        { reference: procedureReference },
        ...existingSupportingInformation.filter(
          ({ reference }) => reference !== procedureReference,
        ),
      ]
    : existingSupportingInformation;

  return {
    ...withoutEditedFields,
    extension,
    quantity: { value: quantity },
    ...(supportingInformation.length ? { supportingInformation } : {}),
  };
}
